import Dexie from 'dexie';

// Create and configure the IndexedDB database
class WealthBagDB extends Dexie {
  constructor() {
    super('WealthBagDB');
    
    // Define database schema
    this.version(1).stores({
      // Products table - stores branch stock products
      products: '++id, product_id, product_sku, product_name, quantity, selling_price, category_name, last_synced, branch_id',
      
      // Customers table - stores customer information
      customers: '++id, customer_id, name, email, phone, address, last_synced',
      
      // Receipts/Sales table - stores completed sales
      receipts: '++id, receipt_id, receipt_number, branch_id, items, total, payment_method, amount_received, change_due, customer, cashier_name, branch_name, created_at, synced, sync_status',
      
      // Sync queue - stores pending API operations
      syncQueue: '++id, endpoint, method, payload, headers, timestamp, retry_count, status, error',
      
      // Auth data - stores authentication tokens and user info
      authData: '++id, access_token, refresh_token, user_info, expires_at, last_synced',
      
      // API cache - stores API responses for offline use (without compound index in v1)
      apiCache: '++id, endpoint, method, params, response, timestamp, expires_at',
    });
    
    // Version 2 - add compound index for apiCache
    this.version(2).stores({
      // API cache with compound index [endpoint+method]
      apiCache: '++id, endpoint, method, [endpoint+method], params, response, timestamp, expires_at',
    }).upgrade(async (tx) => {
      // Migration: re-index existing cache entries
      await tx.apiCache.toArray();
      // Entries will be automatically re-indexed by Dexie during this read
    });

    // Version 3 - add offline credentials store
    this.version(3).stores({
      offlineCredentials: 'email',
    });

    // Version 4 - add exchange rate store (with id as key path - incorrect)
    this.version(4).stores({
      exchangeRate: 'id, current_rate, last_updated',
    });

    // Version 5 - fix exchange rate store: delete and recreate with fixed string key
    // Dexie doesn't support changing primary keys, so we must delete and recreate
    // Using a fixed string key 'current' since we only store one exchange rate
    this.version(5).stores({
      // Remove exchangeRate from version 4
      exchangeRate: null,
    }).upgrade(async (tx) => {
      // Try to preserve existing exchange rate data before deleting
      let savedRate = null;
      try {
        const oldRates = await tx.exchangeRate.toArray();
        if (oldRates && oldRates.length > 0) {
          const latestRate = oldRates[oldRates.length - 1];
          if (latestRate && latestRate.current_rate !== undefined && latestRate.current_rate !== null) {
            savedRate = {
              current_rate: latestRate.current_rate,
              last_updated: latestRate.last_updated || new Date().toISOString(),
            };
          }
        }
      } catch (error) {
        console.warn('[IndexedDB] Could not read old exchange rate during migration:', error);
      }
      
      // Store the rate temporarily in memory to restore after recreation
      if (savedRate) {
        window.__wealthbag_temp_exchange_rate = savedRate;
      }
    });

    // Version 6 - recreate exchange rate store with fixed string key
    this.version(6).stores({
      exchangeRate: 'id, current_rate, last_updated',
    }).upgrade(async (tx) => {
      // Restore the saved exchange rate if it exists
      if (window.__wealthbag_temp_exchange_rate) {
        try {
          await tx.exchangeRate.put({
            id: 'current', // Fixed string key
            current_rate: window.__wealthbag_temp_exchange_rate.current_rate,
            last_updated: window.__wealthbag_temp_exchange_rate.last_updated,
          });
          console.log('[IndexedDB] Restored exchange rate after migration:', window.__wealthbag_temp_exchange_rate);
          delete window.__wealthbag_temp_exchange_rate;
        } catch (error) {
          console.error('[IndexedDB] Failed to restore exchange rate:', error);
        }
      }
    });
  }
}

// Create database instance
const db = new WealthBagDB();

// Helper functions for products
export const productsDB = {
  // Get all products
  async getAll() {
    return await db.products.toArray();
  },
  
  // Get product by ID
  async getById(id) {
    return await db.products.get(id);
  },
  
  // Get product by product_id
  async getByProductId(productId) {
    return await db.products.where('product_id').equals(productId).first();
  },
  
  // Bulk upsert products (for syncing)
  async bulkUpsert(products) {
    return await db.transaction('rw', db.products, async () => {
      for (const product of products) {
        await db.products.put({
          id: product.id,
          product_id: product.product_id || product.product?.id || product.product,
          product_sku: product.product_sku || product.sku,
          product_name: product.product_name || product.name,
          quantity: product.quantity || product.stock,
          selling_price: product.selling_price || product.price,
          category_name: product.category_name || product.category,
          branch_id: product.branch_id,
          last_synced: new Date().toISOString(),
          ...product, // Include all other fields
        });
      }
    });
  },
  
  // Clear all products
  async clear() {
    return await db.products.clear();
  },
};

// Helper functions for customers
export const customersDB = {
  async getAll() {
    return await db.customers.toArray();
  },
  
  async getById(id) {
    return await db.customers.get(id);
  },
  
  async upsert(customer) {
    return await db.customers.put({
      id: customer.id,
      customer_id: customer.customer_id || customer.id,
      name: customer.name,
      email: customer.email,
      phone: customer.phone,
      address: customer.address,
      last_synced: new Date().toISOString(),
      ...customer,
    });
  },
  
  async bulkUpsert(customers) {
    return await db.transaction('rw', db.customers, async () => {
      for (const customer of customers) {
        await this.upsert(customer);
      }
    });
  },
};

// Helper functions for receipts
export const receiptsDB = {
  async getAll() {
    return await db.receipts.toArray();
  },
  
  async getById(id) {
    return await db.receipts.get(id);
  },
  
  async getUnsynced() {
    // Use filter instead of where().equals() to handle undefined/null values
    // This prevents IDBKeyRange errors when synced is undefined
    // Also exclude receipts that are currently being synced (sync_status === 'syncing')
    try {
      const allReceipts = await db.receipts.toArray();
      return allReceipts.filter(receipt => 
        (receipt.synced === false || receipt.synced === undefined || receipt.synced === null) &&
        receipt.sync_status !== 'syncing' // Exclude receipts currently being synced
      );
    } catch (error) {
      console.error('Error in getUnsynced:', error);
      // Fallback: return empty array if there's an error
      return [];
    }
  },
  
  // Fix any receipts with undefined synced field
  async fixSyncedField() {
    try {
      const allReceipts = await db.receipts.toArray();
      const updates = [];
      
      for (const receipt of allReceipts) {
        if (receipt.synced === undefined || receipt.synced === null) {
          updates.push(
            db.receipts.update(receipt.id, { synced: false, sync_status: receipt.sync_status || 'pending' })
          );
        }
      }
      
      if (updates.length > 0) {
        await Promise.all(updates);
        console.log(`Fixed ${updates.length} receipt(s) with undefined synced field`);
      }
    } catch (error) {
      console.error('Error fixing synced field:', error);
    }
  },
  
  async create(receipt) {
    const receiptData = {
      receipt_id: receipt.receipt_id || null,
      receipt_number: receipt.receipt_number || `WB-${Date.now()}`,
      branch_id: receipt.branch_id,
      items: receipt.items,
      total: receipt.total,
      subtotal: receipt.subtotal, // Preserve subtotal
      discount: receipt.discount, // Preserve discount amount
      discount_code: receipt.discount_code || null, // Preserve discount_code (critical for offline sync)
      payment_method: receipt.payment_method,
      amount_received: receipt.amount_received,
      change_due: receipt.change_due,
      customer: receipt.customer || { name: 'Walk-in customer' },
      cashier_name: receipt.cashier_name,
      branch_name: receipt.branch_name,
      created_at: receipt.created_at || new Date().toISOString(),
      synced: false,
      sync_status: 'pending',
      sync_id: receipt.sync_id || null, // Include sync_id if provided
      // Spread any other fields that might be passed
      ...Object.fromEntries(
        Object.entries(receipt).filter(([key]) => 
          !['receipt_id', 'receipt_number', 'branch_id', 'items', 'total', 
            'subtotal', 'discount', 'discount_code', 'payment_method', 
            'amount_received', 'change_due', 'customer', 'cashier_name', 
            'branch_name', 'created_at', 'sync_id'].includes(key)
        )
      ),
    };
    
    // Debug log to verify discount_code is being saved
    if (receiptData.discount_code) {
      console.log('[RECEIPTS DB] 💾 Saving receipt with discount_code:', receiptData.discount_code);
    }
    
    return await db.receipts.add(receiptData);
  },
  
  async markSynced(id, receiptId) {
    return await db.receipts.update(id, {
      receipt_id: receiptId,
      synced: true,
      sync_status: 'synced',
    });
  },
  
  async markSyncFailed(id, error) {
    return await db.receipts.update(id, {
      sync_status: 'failed',
      sync_error: error,
    });
  },
};

// Helper functions for sync queue
export const syncQueueDB = {
  async add(operation) {
    return await db.syncQueue.add({
      endpoint: operation.endpoint,
      method: operation.method,
      payload: operation.payload,
      headers: operation.headers || {},
      timestamp: new Date().toISOString(),
      retry_count: 0,
      status: 'pending',
      ...operation,
    });
  },
  
  async getAllPending() {
    return await db.syncQueue.where('status').equals('pending').toArray();
  },
  
  async markCompleted(id) {
    return await db.syncQueue.update(id, {
      status: 'completed',
    });
  },
  
  async markFailed(id, error) {
    return await db.syncQueue.update(id, {
      status: 'failed',
      error: error?.message || String(error),
      retry_count: (await db.syncQueue.get(id))?.retry_count + 1 || 1,
    });
  },
  
  async delete(id) {
    return await db.syncQueue.delete(id);
  },
};

// Helper functions for auth data
export const authDB = {
  async get() {
    return await db.authData.orderBy('id').reverse().first();
  },
  
  async save(authData) {
    // Clear old auth data
    await db.authData.clear();
    
    // Save new auth data
    return await db.authData.add({
      access_token: authData.access_token,
      refresh_token: authData.refresh_token,
      user_info: authData.user_info,
      expires_at: authData.expires_at || new Date(Date.now() + 6 * 60 * 60 * 1000).toISOString(),
      last_synced: new Date().toISOString(),
    });
  },
  
  async clear() {
    return await db.authData.clear();
  },
};

// Helper functions for offline credentials
export const offlineCredentialsDB = {
  async save(credential) {
    if (!credential?.email) {
      return null;
    }
    
    return await db.offlineCredentials.put({
      email: credential.email.toLowerCase(),
      password_hash: credential.password_hash,
      access_token: credential.access_token,
      user_info: credential.user_info,
      refresh_token_encrypted: credential.refresh_token_encrypted,
      refresh_token_iv: credential.refresh_token_iv,
      refresh_token_alg: credential.refresh_token_alg,
      access_expires_at: credential.access_expires_at,
      updated_at: new Date().toISOString(),
    });
  },
  
  async getByEmail(email) {
    if (!email) return null;
    return await db.offlineCredentials.get(email.toLowerCase());
  },
  
  async delete(email) {
    if (!email) return;
    return await db.offlineCredentials.delete(email.toLowerCase());
  },
  
  async clear() {
    return await db.offlineCredentials.clear();
  },
};

// Helper functions for API cache
export const apiCacheDB = {
  async get(endpoint, method = 'GET', params = {}) {
    try {
      const canUseCompoundKey = typeof endpoint === 'string' && endpoint.length > 0 &&
        typeof method === 'string' && method.length > 0;
      if (!canUseCompoundKey) {
        throw new Error('Invalid compound key');
      }
      // Try using compound index if available (v2+)
      const cached = await db.apiCache
        .where('[endpoint+method]')
        .equals([endpoint, method])
        .first();
      
      if (cached && cached.expires_at && new Date(cached.expires_at) > new Date()) {
        return cached.response;
      }
    } catch (error) {
      // Fallback: query by endpoint and filter by method (v1)
      const allCached = await db.apiCache
        .where('endpoint')
        .equals(endpoint)
        .filter(item => item.method === method)
        .toArray();
      
      // Find non-expired cache
      const validCache = allCached.find(
        item => !item.expires_at || new Date(item.expires_at) > new Date()
      );
      
      if (validCache) {
        return validCache.response;
      }
    }
    
    return null;
  },
  
  async set(endpoint, method, params, response, ttlMinutes = 60) {
    const expiresAt = new Date(Date.now() + ttlMinutes * 60 * 1000).toISOString();
    
    try {
      const canUseCompoundKey = typeof endpoint === 'string' && endpoint.length > 0 &&
        typeof method === 'string' && method.length > 0;
      if (!canUseCompoundKey) {
        throw new Error('Invalid compound key');
      }
      // Try using compound index if available (v2+)
      await db.apiCache
        .where('[endpoint+method]')
        .equals([endpoint, method])
        .delete();
    } catch (error) {
      // Fallback: delete by endpoint and filter by method (v1)
      const toDelete = await db.apiCache
        .where('endpoint')
        .equals(endpoint)
        .filter(item => item.method === method)
        .toArray();
      
      for (const item of toDelete) {
        await db.apiCache.delete(item.id);
      }
    }
    
    return await db.apiCache.add({
      endpoint,
      method,
      params: JSON.stringify(params),
      response,
      timestamp: new Date().toISOString(),
      expires_at: expiresAt,
    });
  },
  
  async clear() {
    return await db.apiCache.clear();
  },
  
  async clearExpired() {
    const now = new Date().toISOString();
    return await db.apiCache.where('expires_at').below(now).delete();
  },
};

// Helper functions for exchange rate
export const exchangeRateDB = {
  async get() {
    // Use fixed key 'current' since we only store one exchange rate
    return await db.exchangeRate.get('current');
  },
  
  async save(rate) {
    // Use put with fixed key 'current' - this will update if exists, create if not
    return await db.exchangeRate.put({
      id: 'current', // Fixed string key
      current_rate: rate.current_rate || rate,
      last_updated: new Date().toISOString(),
    });
  },
  
  async clear() {
    return await db.exchangeRate.clear();
  },
};

// Export database instance
export default db;

